import { Card, CardBody, CardHeader, Pill } from '@hvac/ui';
import { useQuery } from '@tanstack/react-query';
import { connectorCatalogQuery } from '../../shared/api/connection-queries.js';
import { connectionLine } from './connection-text.js';
import { CatalogTile } from './connections-page.js';
import { ConnectorMark } from './connector-mark.js';

/**
 * Setup's second step: connect the CRM the business runs on, so the first
 * day opens with its customers, jobs and invoices rather than an empty
 * screen. A CRM without a connector is not a dead end; it comes in as a
 * spreadsheet once the books are open.
 */
export function SetupConnections({ organizationId }: { organizationId: string }) {
  const catalog = useQuery(connectorCatalogQuery(organizationId));
  const entries = (catalog.data?.connectors ?? []).filter(
    (entry) => entry.availability !== 'coming_soon',
  );

  return (
    <Card>
      <CardHeader
        title="Connect the CRM"
        description="Customers, jobs and invoices come in by themselves and stay current every night. Using something else? Import its invoice export from Connections once the books are open."
      />
      <CardBody>
        <div className="grid gap-4 md:grid-cols-2">
          {entries.map((entry) =>
            entry.connection !== null && entry.connection.status !== 'disconnected' ? (
              <div
                key={entry.provider}
                className="flex items-center gap-3 rounded-[var(--radius-surface)] border border-success/30 bg-success-surface/40 p-4"
              >
                <ConnectorMark kind={entry.provider} />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-content">{entry.name}</p>
                  <p className="truncate text-xs text-content-muted">
                    {connectionLine(entry.connection)}
                  </p>
                </div>
                <Pill tone="success">Connected</Pill>
              </div>
            ) : (
              <CatalogTile
                key={entry.provider}
                organizationId={organizationId}
                entry={entry}
                canManage
              />
            ),
          )}
        </div>
      </CardBody>
    </Card>
  );
}
